import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import BadgesShowcase from '../components/BadgesShowcase';
import { Trophy, Flame, Star, Award, Crown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const rankStyle = (rank) => {
  if (rank === 1) return { bg: '#FFE66D', icon: Crown };
  if (rank === 2) return { bg: '#E5E7EB', icon: Trophy };
  if (rank === 3) return { bg: '#FCD9B6', icon: Award };
  return { bg: 'transparent', icon: null };
};

const Leaderboard = () => {
  const { getAuthHeader, user } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/leaderboard`, { headers: user ? getAuthHeader() : {} })
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <>
      <Helmet>
        <title>{t('leaderboard.title')} - Daily Wit</title>
      </Helmet>
      <div className="min-h-screen bg-[#FFFDF5] dark:bg-[#1a1816] py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Trophy className="w-9 h-9 text-[#FFE66D] fill-[#FFE66D]" strokeWidth={2} />
              <h1 className="text-4xl md:text-5xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
                {t('leaderboard.title')}
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 max-w-xl mx-auto">{t('leaderboard.subtitle')}</p>
          </div>

          {/* Your rank card */}
          {data?.me && (
            <div
              data-testid="my-rank-card"
              className="flex items-center justify-between bg-gradient-to-r from-[#FF6B6B] to-[#4ECDC4] p-1 rounded-xl mb-6 hard-shadow-lg"
            >
              <div className="flex items-center justify-between w-full bg-white dark:bg-[#2D2A26] rounded-lg px-5 py-4">
                <div>
                  <p className="text-xs font-bold uppercase text-gray-500 dark:text-gray-400">{t('leaderboard.yourRank')}</p>
                  <p className="text-3xl font-bold dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>#{data.me.rank}</p>
                </div>
                <div className="flex items-center gap-5">
                  <span className="flex items-center gap-1.5 font-bold dark:text-white"><Star className="w-5 h-5 fill-[#FFE66D] text-[#FFE66D]" />{data.me.points}</span>
                  <span className="flex items-center gap-1.5 font-bold text-[#FF6B6B]"><Flame className="w-5 h-5 fill-[#FF6B6B]" />{data.me.streak_days}</span>
                </div>
              </div>
            </div>
          )}

          {!user && (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-6" data-testid="leaderboard-login-prompt">
              {t('leaderboard.loginPrompt')}
            </p>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" strokeWidth={2.5} />
            </div>
          ) : !data || data.leaderboard.length === 0 ? (
            <p className="text-center text-lg text-gray-600 dark:text-gray-400 py-12" data-testid="leaderboard-empty">
              {t('leaderboard.empty')}
            </p>
          ) : (
            <div className="bg-white dark:bg-[#2D2A26] border-2 border-black dark:border-white rounded-xl overflow-hidden hard-shadow-lg mb-12" data-testid="leaderboard-list">
              {data.leaderboard.map((row) => {
                const { bg, icon: RankIcon } = rankStyle(row.rank);
                return (
                  <div
                    key={row.rank}
                    data-testid={`leaderboard-row-${row.rank}`}
                    className="flex items-center justify-between px-4 sm:px-6 py-3 border-b-2 border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className="w-9 h-9 flex items-center justify-center rounded-full border-2 border-black dark:border-white font-bold flex-shrink-0"
                        style={{ backgroundColor: bg !== 'transparent' ? bg : undefined }}
                      >
                        {RankIcon ? <RankIcon className="w-5 h-5 text-black" strokeWidth={2.5} /> : <span className="dark:text-white">{row.rank}</span>}
                      </div>
                      <span className="font-bold dark:text-white truncate">{row.display_name}</span>
                      {row.badge_count > 0 && (
                        <span className="hidden sm:flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Award className="w-3.5 h-3.5" /> {row.badge_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4 sm:gap-5 flex-shrink-0">
                      <span className="flex items-center gap-1 font-bold text-sm dark:text-white">
                        <Star className="w-4 h-4 fill-[#FFE66D] text-[#FFE66D]" />{row.points}
                      </span>
                      <span className="flex items-center gap-1 font-bold text-sm text-[#FF6B6B]">
                        <Flame className="w-4 h-4 fill-[#FF6B6B]" />{row.streak_days}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <BadgesShowcase />
        </div>
      </div>
    </>
  );
};

export default Leaderboard;
